import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { getFromSession, setToSession } from 'helpers/session';

const Wrapper = styled.div`
    position: fixed;
    left: 50%;
    bottom: 16px;
    transform: translateX(-50%);
    z-index: 1080;
    width: min(560px, calc(100vw - 24px));
    pointer-events: none;

    .banner {
        pointer-events: auto;
        background: #fff8e1;
        border: 1px solid #f0c36d;
        border-left: 4px solid #d6940a;
        border-radius: 8px;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
        padding: 12px 14px;
        display: flex;
        align-items: flex-start;
        gap: 12px;
        color: #5a4203;
        font-size: 14px;
        line-height: 1.4;
    }

    .icon {
        flex: 0 0 auto;
        font-size: 20px;
        line-height: 1;
        margin-top: 1px;
        color: #d6940a;
    }

    .content {
        flex: 1 1 auto;
        min-width: 0;

        .title {
            font-weight: 600;
            margin: 0 0 2px 0;
        }

        .body {
            margin: 0;
            color: #6b5208;
        }
    }

    .toggle {
        flex: 0 0 auto;
        background: transparent;
        border: 0;
        color: #6b5208;
        padding: 2px 6px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 16px;
        line-height: 1;

        &:hover { background: rgba(0, 0, 0, 0.05); }
    }

    &.minimized {
        width: auto;

        .banner {
            pointer-events: auto;
            padding: 6px 10px;
            font-size: 13px;
            cursor: pointer;
            gap: 8px;
            white-space: nowrap;
        }
        .content { display: none; }
        .toggle { display: none; }
        .pill-text {
            font-weight: 600;
        }
    }

    @media (max-width: 575px) {
        width: calc(100vw - 16px);
        bottom: 8px;

        .banner { font-size: 13px; }
    }
`;

export default function ClosedFiscalYearBanner() {
    const [year, setYear] = useState(() => {
        const stored = getFromSession('navbar-fiscalyear');
        return stored != null ? Number(stored) : null;
    });
    const [status, setStatus] = useState(() => getFromSession('navbar-fiscalyear-status') ?? null);
    const [minimized, setMinimized] = useState(false);
    const lastYearRef = useRef(year);

    useEffect(() => {
        const handler = (ev) => {
            const nextYear = ev?.detail?.year ?? null;
            const nextStatus = ev?.detail?.status ?? null;
            setYear(nextYear != null ? Number(nextYear) : null);
            setStatus(nextStatus);
        };
        document.addEventListener('fiscalYearChanged', handler);
        return () => document.removeEventListener('fiscalYearChanged', handler);
    }, []);

    useEffect(() => {
        if ( year !== lastYearRef.current ) {
            setMinimized(false);
            lastYearRef.current = year;
            return;
        }
        const stored = getFromSession('closed-fy-banner-minimized');
        const storedYear = getFromSession('closed-fy-banner-minimized-year');
        if ( stored === '1' && Number(storedYear) === Number(year) ) {
            setMinimized(true);
        }
    }, [year, status]);

    if ( status !== 'closed' ) return null;

    const toggle = () => {
        const next = !minimized;
        setMinimized(next);
        setToSession('closed-fy-banner-minimized', next ? '1' : '0');
        setToSession('closed-fy-banner-minimized-year', String(year));
    };

    return (
        <Wrapper className={minimized ? 'minimized' : ''}>
            <div
                className="banner"
                role="status"
                aria-live="polite"
                onClick={minimized ? toggle : undefined}
                title={minimized ? 'Mostrar aviso' : ''}
            >
                <i className="bi bi-lock-fill icon"></i>
                { minimized ? (
                    <span className="pill-text">Ejercicio {year} cerrado</span>
                ) : (
                    <div className="content">
                        <p className="title">Ejercicio fiscal {year} cerrado</p>
                        <p className="body">
                            Los movimientos que registres (recibos, facturas, conciliaciones…) <strong>no generarán asientos contables</strong>. Reábrelo si necesitas que la contabilidad refleje los cambios.
                        </p>
                    </div>
                ) }
                { !minimized && (
                    <button
                        type="button"
                        className="toggle"
                        onClick={toggle}
                        title="Minimizar"
                        aria-label="Minimizar aviso"
                    >
                        <i className="bi bi-dash-lg"></i>
                    </button>
                ) }
            </div>
        </Wrapper>
    );
}
